import React, { useRef, useEffect, useState } from "react";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import CarImage from "../../assets/img/hero/Hero-Background.jpg";
import FoundersImage from "../../assets/img/KrisKatrien.jpg";
import Logo from "../../assets/img/logo.svg";

// All recognised insurers
import AGLogo      from "../../assets/img/erkenningen/AG.0290bba3ce408ce766b3-removebg-preview.png";
import AllianzLogo from "../../assets/img/erkenningen/Allianz.jpg";
import AxaLogo     from "../../assets/img/erkenningen/AXA.png";
import ArgentaLogo from "../../assets/img/erkenningen/Argenta.png";
import BaloiserLogo from "../../assets/img/erkenningen/Baloise.png";
import BelfiusLogo from "../../assets/img/erkenningen/Belfius.jpg";
import DvvLogo     from "../../assets/img/erkenningen/Dvv.jpg";
import EthiasLogo  from "../../assets/img/erkenningen/Ethias.png";
import NNLogo      from "../../assets/img/erkenningen/NN.jpg";
import YuzzuLogo   from "../../assets/img/erkenningen/YUZZU.png";
import KbcLogo     from "../../assets/img/erkenningen/cq5dam.web.480.9999.png";
import ViviumLogo  from "../../assets/img/erkenningen/vivium-pv.png";

import "./Hero.css";

const LUXURY = [0.16, 1, 0.3, 1];

const INSURERS = [
  { src: AllianzLogo,  alt: "Allianz" },
  { src: AxaLogo,      alt: "AXA" },
  { src: BelfiusLogo,  alt: "Belfius" },
  { src: BaloiserLogo, alt: "Baloise" },
  { src: NNLogo,       alt: "NN" },
  { src: EthiasLogo,   alt: "Ethias" },
  { src: AGLogo,       alt: "AG Insurance" },
  { src: ArgentaLogo,  alt: "Argenta" },
  { src: DvvLogo,      alt: "DVV" },
  { src: YuzzuLogo,    alt: "YUZZU" },
  { src: KbcLogo,      alt: "KBC" },
  { src: ViviumLogo,   alt: "Vivium" },
];

// Stagger container — each child slides up with 0.18s delay between items
const textContainerVariants = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.18, delayChildren: 1.0 },
  },
};

const textItemVariants = {
  hidden: { opacity: 0, y: 20, clipPath: "inset(100% 0 0 0)" },
  visible: {
    opacity: 1,
    y: 0,
    clipPath: "inset(0% 0 0 0)",
    transition: { ease: LUXURY, duration: 0.85 },
  },
};

export default function Hero() {
  const containerRef = useRef(null);
  const { scrollY } = useScroll();
  const isInView = useInView(containerRef, { once: true, margin: "-5%" });
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  const imageY = useTransform(scrollY, [0, 600], [0, isMobile ? -10 : -40]);

  return (
    <div id="home" className="hero-section" ref={containerRef}>

      {/* ── Left text panel ── */}
      <div className="hero-panel">
        <motion.div
          className="hero-panel-inner"
          variants={textContainerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >

          {/* 2px red accent line — red only as punctuation */}
          <motion.div className="hero-accent-line" variants={textItemVariants} />

          {/* Brand */}
          <motion.div className="hero-brand" variants={textItemVariants}>
            <span className="name-carrosserie">
              Carr
              <span className="logo-o-container">
                <img src={Logo} alt="" className="logo-o" />
              </span>
              sserie
            </span>
            <span className="name-kris">Kris</span>
          </motion.div>

          {/* CTA — ghost button */}
          <motion.div variants={textItemVariants} className="hero-cta-group">
            <button
              className="hero-cta-primary"
              onClick={() => {
                const el = document.getElementById("contact");
                if (el) el.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Contacteer ons
            </button>

            {/* Direct contact — address + phone + email */}
            <div className="hero-direct-contacts">
              <a
                href="https://maps.google.com/?q=Herentalse+Steenweg+89A,+2280+Grobbendonk"
                target="_blank"
                rel="noopener noreferrer"
                className="hero-direct-link"
              >
                <span className="hero-direct-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z"/>
                    <circle cx="12" cy="10" r="3"/>
                  </svg>
                </span>
                <span>Herentalse Steenweg 89A, Grobbendonk</span>
              </a>
              <span className="hero-direct-sep" aria-hidden="true" />
              <a href="tel:+32497284662" className="hero-direct-link">
                <span className="hero-direct-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 14.92v2z"/>
                  </svg>
                </span>
                <span>+32 497 28 46 62</span>
              </a>
              <span className="hero-direct-sep" aria-hidden="true" />
              <a href="mailto:kris@carrosseriekris.be" className="hero-direct-link">
                <span className="hero-direct-icon">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="4" width="20" height="16" rx="0"/>
                    <path d="M22 4L12 13 2 4"/>
                  </svg>
                </span>
                <span>kris@carrosseriekris.be</span>
              </a>
            </div>
          </motion.div>

          {/* Founders glass card */}
          <motion.div className="founders-card" variants={textItemVariants}>
            <div className="founders-photo-wrap">
              <img src={FoundersImage} alt="Kris en Katrien" className="founders-photo" />
            </div>
            <div className="founders-details">
              <span className="founders-name">Kris &amp; Katrien</span>
              <span className="founders-role">Zaakvoerders</span>
              <span className="founders-since">Grobbendonk · Sinds 2018</span>
            </div>
          </motion.div>

        </motion.div>
      </div>

      {/* ── Right image column with curtain reveal ── */}
      <div className="hero-image-col">

        {/* Subtle left-edge fade — blends panel into image */}
        <div className="hero-image-edge-fade" />

        {/* Parallax car image */}
        <motion.div
          className="hero-image-wrapper"
          style={{ y: imageY }}
          initial={{ scale: 1.08 }}
          animate={{ scale: 1.0 }}
          transition={{ duration: 2.4, ease: LUXURY }}
        >
          <img src={CarImage} alt="Carrosserie Kris" className="hero-car-image" />
        </motion.div>

        {/* Curtain — slides right to unveil the image */}
        <motion.div
          className="hero-curtain"
          initial={{ x: "0%" }}
          animate={{ x: "101%" }}
          transition={{ duration: 1.1, ease: LUXURY, delay: 0.15 }}
        />

      </div>

      {/* ── Bottom trust strip — infinite marquee ── */}
      <motion.div
        className="hero-trust-strip"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ duration: 0.8, delay: 1.5, ease: LUXURY }}
      >
        <span className="trust-label">Erkend door</span>
        <div className="trust-track-wrapper">
          {/* Triple logos: translateX(-33.333%) = seamless loop */}
          <div className="trust-track">
            {[...INSURERS, ...INSURERS, ...INSURERS].map((ins, i) => (
              <img key={i} src={ins.src} alt={ins.alt} className="trust-logo" title={ins.alt} />
            ))}
          </div>
        </div>
      </motion.div>

    </div>
  );
}
